import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../../components/AdminLayout';
import { adminApi, SCHOOL_TYPES, StateConfig, StatePatch } from '../../api/admin';

const StatesSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: states, isLoading } = useQuery<StateConfig[]>({
    queryKey: ['states'],
    queryFn: adminApi.listStates,
  });

  const mutation = useMutation({
    mutationFn: ({ code, patch }: { code: string; patch: StatePatch }) =>
      adminApi.updateState(code, patch),
    onSuccess: (updated) => {
      // Replace just the changed row in the cached roster.
      queryClient.setQueryData<StateConfig[]>(['states'], (prev) =>
        (prev ?? []).map((s) => (s.id === updated.id ? updated : s)),
      );
    },
  });

  const patchState = (state: StateConfig, patch: StatePatch) =>
    mutation.mutate({ code: state.abbreviated_name, patch });

  const title = 'States';
  const subtitle = (
    <>
      Configure your posture in each state. Changes save immediately. Whether a
      state has opted into the program is set by SGOKit and shown here read-only.
    </>
  );

  // The schema every state's content shares — designed in the form builder
  // (see docs/plans/state-content.md); each row's "Edit content" fills it in.
  const actions = (
    <Link
      to="/settings/states/content"
      className="sgk-btn sgk-btn--gold sgk-btn--sm"
      data-testid="states-configure-content-link"
    >
      Configure content
    </Link>
  );

  if (isLoading || !states) {
    return (
      <AdminLayout title={title} subtitle={subtitle} actions={actions} wide>
        <p className="sgk-admin__sub" data-testid="states-loading">Loading…</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={title} subtitle={subtitle} actions={actions} wide>
      <div className="sgk-table-wrap">
        <div className="sgk-table-scroll">
          <table className="sgk-table">
            <thead>
              <tr>
                <th>State</th>
                <th>Opted in</th>
                <th>SGO registered</th>
                <th>Eligible school types</th>
                <th>Content</th>
              </tr>
            </thead>
            <tbody>
              {states.map((state) => {
                // Three tiers, mirroring the public map: registered, opted-in but
                // not registered, or outside the program. SGOKit owns opted_in;
                // the tenant's registration is independent of it.
                const tier = state.sgo_registered
                  ? 'is-registered'
                  : state.opted_in
                    ? 'is-opted'
                    : 'is-out';
                return (
                <tr
                  key={state.id}
                  className={`sgk-srow ${tier}`}
                  data-testid={`state-row-${state.abbreviated_name}`}
                >
                  <td className="sgk-table__state">
                    {state.full_name} <span className="sgk-table__abbr">{state.abbreviated_name}</span>
                  </td>
                  <td>
                    <span
                      className={`sgk-status${state.opted_in ? ' is-on' : ''}`}
                      data-testid={`state-opted-in-status-${state.abbreviated_name}`}
                      title="Set by SGOKit"
                    >
                      {state.opted_in ? 'Opted in' : 'Not in program'}
                    </span>
                  </td>
                  <td>
                    <label className="sgk-toggle">
                      <input
                        type="checkbox"
                        checked={state.sgo_registered}
                        onChange={(e) => patchState(state, { sgo_registered: e.target.checked })}
                        data-testid={`state-sgo-registered-checkbox-${state.abbreviated_name}`}
                      />
                      <span className="sgk-toggle__track" />
                    </label>
                  </td>
                  <td>
                    <span
                      data-testid={`state-school-types-${state.abbreviated_name}`}
                      title="Set by SGOKit"
                    >
                      {SCHOOL_TYPES.filter((t) => state.eligible_school_types.includes(t.value))
                        .map((t) => t.label)
                        .join(', ') || '—'}
                    </span>
                  </td>
                  <td>
                    <Link
                      to={`/settings/states/${state.abbreviated_name.toLowerCase()}/content`}
                      data-testid={`state-edit-content-link-${state.abbreviated_name}`}
                    >
                      Edit content
                    </Link>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default StatesSettings;
